import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useQuery } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { Empty } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { XStack, YStack } from '@/components/ui/layout/layout';
import TextArea from '@/components/ui/TextArea';
import { Text } from '@/components/ui/typography';
import { userStore } from '@/stores/user.store';
import { trackEvent } from '@/utils/analytics';

import {
  DefaultNotificationHour,
  type JournalConfig,
  JournalLanguage,
  JournalNotificationTime,
  JournalNotificationType,
  type JournalSection,
  JournalSectionType,
  suggestedSectionsQuery,
} from './journal-queries';

export const SectionTypeSelector = ({
  selectedType,
  onChange,
}: {
  selectedType: JournalSectionType;
  onChange: (type: JournalSectionType) => void;
}) => {
  return (
    <YStack className="gap-2">
      <Label className="text-foreground">Section Type</Label>
      <XStack className="gap-4 mt-1">
        <Button
          type="button"
          onClick={() => onChange(JournalSectionType.PARAGRAPHS)}
          variant={
            selectedType === JournalSectionType.PARAGRAPHS ? 'default' : 'ghost'
          }
          className="flex items-center gap-2 p-2"
        >
          <Icons.type className="size-4" />
          Paragraphs
        </Button>
        <Button
          type="button"
          onClick={() => onChange(JournalSectionType.POINTS)}
          variant={
            selectedType === JournalSectionType.POINTS ? 'default' : 'ghost'
          }
          className="flex items-center gap-2 p-2"
        >
          <Icons.list className="size-4" />
          Bullets
        </Button>
      </XStack>
    </YStack>
  );
};

export const AddSectionForm = ({
  title,
  setTitle,
  description,
  setDescription,
  type,
  setType,
  onSave,
  onCancel,
}: {
  title: string;
  setTitle: (value: string) => void;
  description: string;
  setDescription: (value: string) => void;
  type: JournalSectionType;
  setType: (type: JournalSectionType) => void;
  onSave: () => void;
  onCancel: () => void;
}) => {
  const titleInputRef = useRef<HTMLInputElement>(null);
  const saveButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (titleInputRef.current) {
      titleInputRef.current.focus();
    }

    setTimeout(() => {
      if (saveButtonRef.current) {
        saveButtonRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
        });
      }
    }, 100);
  }, []);

  return (
    <YStack className="bg-surface-hover rounded-md p-4 mt-2">
      <YStack className="gap-4">
        <Input
          label="Section Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g., Daily Summary"
          ref={titleInputRef}
        />
        <YStack className="gap-2">
          <Label>Description</Label>
          <TextArea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g., Summarize all my conversations from Slack"
            className="h-24 resize-none"
          />
        </YStack>
        <SectionTypeSelector selectedType={type} onChange={setType} />
        <XStack className="justify-end gap-2 mt-2">
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={onSave} ref={saveButtonRef}>
            Add Section
          </Button>
        </XStack>
      </YStack>
    </YStack>
  );
};

export const EditSectionForm = ({
  title,
  setTitle,
  description,
  setDescription,
  type,
  setType,
  onSave,
  onCancel,
}: {
  title: string;
  setTitle: (value: string) => void;
  description: string;
  setDescription: (value: string) => void;
  type: JournalSectionType;
  setType: (type: JournalSectionType) => void;
  onSave: () => void;
  onCancel: () => void;
}) => {
  const titleInputRef = useRef<HTMLInputElement>(null);
  const saveButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (titleInputRef.current) {
      titleInputRef.current.focus();

      setTimeout(() => {
        if (saveButtonRef.current) {
          saveButtonRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'end',
          });
        }
      }, 100);
    }
  }, []);

  return (
    <YStack className="bg-surface-hover rounded-md p-4 mt-1 border-t border-border">
      <YStack className="gap-2">
        <Input
          label="Section Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g., Daily Summary"
          ref={titleInputRef}
        />
        <YStack className="gap-2">
          <Label>Description</Label>
          <TextArea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g., Summarize all my conversations from Slack"
            className="h-24 resize-none"
          />
        </YStack>
        <SectionTypeSelector selectedType={type} onChange={setType} />
        <XStack className="justify-end gap-2">
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={onSave} ref={saveButtonRef}>
            Save Changes
          </Button>
        </XStack>
      </YStack>
    </YStack>
  );
};

export const DeleteSectionConfirmation = ({
  onCancel,
  onConfirm,
}: {
  onCancel: () => void;
  onConfirm: () => void;
}) => {
  const confirmButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (confirmButtonRef.current) {
      setTimeout(() => {
        if (confirmButtonRef.current) {
          confirmButtonRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'end',
          });
        }
      }, 100);
    }
  }, []);

  return (
    <YStack className="bg-surface-hover rounded-md p-3 mt-1 border-t border-border">
      <YStack className="gap-2">
        <Text className="text-destructive text-foreground">
          Are you sure you want to delete this section?
        </Text>
        <Text lowContrast size="1" className="text-foreground">
          This action cannot be undone.
        </Text>
        <XStack className="justify-end gap-2 mt-2">
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            ref={confirmButtonRef}
          >
            Delete
          </Button>
        </XStack>
      </YStack>
    </YStack>
  );
};

export const JournalSectionItem = ({
  section,
  onEdit,
  onDelete,
  isDisabled,
  deletingSectionId,
  editingSectionId,
  onCancelDelete,
  onConfirmDelete,
  editSectionTitle,
  setEditSectionTitle,
  editSectionDescription,
  setEditSectionDescription,
  editSectionType,
  setEditSectionType,
  onCancelEdit,
  onSaveEdit,
}: {
  section: JournalSection;
  index: number;
  onEdit: (section: JournalSection) => void;
  onDelete: (id: string) => void;
  isDisabled: boolean;
  deletingSectionId: string | null;
  editingSectionId: string | null;
  onCancelDelete: () => void;
  onConfirmDelete: (id: string) => void;
  editSectionTitle: string;
  setEditSectionTitle: (value: string) => void;
  editSectionDescription: string;
  setEditSectionDescription: (value: string) => void;
  editSectionType: JournalSectionType;
  setEditSectionType: (value: JournalSectionType) => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id, disabled: isDisabled });

  const style = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const buttonsDisabled =
    isDisabled ||
    (deletingSectionId !== null && deletingSectionId !== section.id);

  return (
    <YStack ref={setNodeRef} style={style}>
      <XStack
        className="justify-between items-center bg-surface-hover rounded-md p-1 cursor-grab touch-none"
        {...attributes}
        {...listeners}
      >
        <XStack className="items-center gap-2 p-2">
          {section.type === JournalSectionType.POINTS ? (
            <Icons.list className="size-4 text-foreground" />
          ) : (
            <Icons.type className="size-4 text-foreground" />
          )}
          <Text className="text-foreground text-sm">{section.title}</Text>
        </XStack>
        <XStack className="gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onEdit(section)}
            onPointerDown={(e) => e.stopPropagation()}
            title="Edit section"
            disabled={buttonsDisabled}
            className="text-foreground"
          >
            <Icons.edit />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onDelete(section.id)}
            onPointerDown={(e) => e.stopPropagation()}
            title="Delete section"
            disabled={buttonsDisabled}
            className="text-foreground"
          >
            <Icons.trash />
          </Button>
        </XStack>
      </XStack>

      {deletingSectionId === section.id && (
        <DeleteSectionConfirmation
          onCancel={onCancelDelete}
          onConfirm={() => onConfirmDelete(section.id)}
        />
      )}

      {editingSectionId === section.id && (
        <EditSectionForm
          title={editSectionTitle}
          setTitle={setEditSectionTitle}
          description={editSectionDescription}
          setDescription={setEditSectionDescription}
          type={editSectionType}
          setType={setEditSectionType}
          onSave={onSaveEdit}
          onCancel={onCancelEdit}
        />
      )}
    </YStack>
  );
};

export const SuggestedSections = ({
  suggestions,
  existingSections,
  onAddSuggestion,
}: {
  suggestions: JournalSection[];
  existingSections: JournalSection[];
  onAddSuggestion: (title: string) => void;
}) => {
  if (!suggestions || suggestions.length === 0) {
    return null;
  }

  const filteredSuggestions = suggestions
    .filter(
      (suggestion) =>
        !existingSections.some(
          (section) =>
            section.title.toLowerCase() === suggestion.title.toLowerCase()
        )
    )
    .slice(0, 3);

  if (filteredSuggestions.length === 0) {
    return null;
  }

  return (
    <YStack className="mt-4">
      <Text className="text-foreground mb-2">Suggested Sections</Text>
      <XStack className="flex-wrap gap-2">
        {filteredSuggestions.map((suggestion) => (
          <Button
            key={suggestion.id}
            variant="outline"
            size="sm"
            className="border-dashed border-foreground text-foreground hover:text-foreground"
            onClick={() => onAddSuggestion(suggestion.title)}
          >
            + {suggestion.title}
          </Button>
        ))}
      </XStack>
    </YStack>
  );
};

export const JournalSectionsSettings = observer(
  function JournalSectionsSettings() {
    const user = userStore.user;

    const journalConfig = user?.journal_config || {
      notification_time: user?.journal_notification_time,
      notification_hour: DefaultNotificationHour,
      notification_type: JournalNotificationType.TLDR,
      expand_sections: true,
      sections: [],
      language: JournalLanguage.ENGLISH,
      mobile_notifications_enabled: true,
    };

    const [sections, setSections] = useState<JournalSection[]>(
      journalConfig.sections || []
    );

    const sensors = useSensors(
      useSensor(PointerSensor, {
        activationConstraint: {
          distance: 8,
        },
      }),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
      })
    );

    const handleDragEnd = (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = sections.findIndex(
        (section) => section.id === active.id
      );
      const newIndex = sections.findIndex((section) => section.id === over.id);

      const updatedSections = [...sections];
      const [movedSection] = updatedSections.splice(oldIndex, 1);
      updatedSections.splice(newIndex, 0, movedSection);

      setSections(updatedSections);
      updateUserConfig(updatedSections);
    };

    const [editingSectionId, setEditingSectionId] = useState<string | null>(
      null
    );
    const [isAddingSection, setIsAddingSection] = useState(false);
    const [editSectionTitle, setEditSectionTitle] = useState('');
    const [editSectionDescription, setEditSectionDescription] = useState('');
    const [editSectionType, setEditSectionType] = useState<JournalSectionType>(
      JournalSectionType.PARAGRAPHS
    );
    const [deletingSectionId, setDeletingSectionId] = useState<string | null>(
      null
    );

    const { data: suggestedSections = [] } = useQuery(suggestedSectionsQuery());

    const updateUserConfig = (updatedSections: JournalSection[]) => {
      if (user) {
        const newConfig: JournalConfig = {
          ...journalConfig,
          notification_time:
            journalConfig.notification_time || JournalNotificationTime.MORNING,
          notification_hour:
            journalConfig.notification_hour || DefaultNotificationHour,
          notification_type:
            journalConfig.notification_type || JournalNotificationType.TLDR,
          expand_sections: journalConfig.expand_sections,
          language: journalConfig.language || JournalLanguage.ENGLISH,
          mobile_notifications_enabled:
            journalConfig.mobile_notifications_enabled ?? true,
          sections: updatedSections,
        };

        userStore.syncUpdate({
          journal_config: newConfig,
        });

        trackEvent('USER_ACTION_EVENT');
      }
    };

    const handleAddSection = () => {
      setEditingSectionId(null);
      setEditSectionTitle('');
      setEditSectionDescription('');
      setEditSectionType(JournalSectionType.PARAGRAPHS);
      setIsAddingSection(true);
    };

    const handleEditSection = (section: JournalSection) => {
      setIsAddingSection(false);
      setDeletingSectionId(null);
      setEditingSectionId(section.id);
      setEditSectionTitle(section.title);
      setEditSectionDescription(section.description);
      setEditSectionType(section.type);
    };

    const handleCancelEdit = () => {
      setEditingSectionId(null);
      setIsAddingSection(false);
    };

    const handleSaveSection = () => {
      if (!editSectionTitle.trim()) return;

      let updatedSections: JournalSection[];

      if (editingSectionId) {
        updatedSections = sections.map((section) =>
          section.id === editingSectionId
            ? {
                id: section.id,
                title: editSectionTitle,
                description: editSectionDescription,
                type: editSectionType,
              }
            : section
        );
      } else {
        const newSection: JournalSection = {
          id: uuidv4(),
          title: editSectionTitle,
          description: editSectionDescription,
          type: editSectionType,
        };
        updatedSections = [...sections, newSection];
      }

      setSections(updatedSections);
      updateUserConfig(updatedSections);
      setEditingSectionId(null);
      setIsAddingSection(false);
    };

    const handleAddSuggestedSection = (title: string) => {
      const suggestedSection = suggestedSections.find(
        (section: JournalSection) =>
          section.title.toLowerCase() === title.toLowerCase()
      );

      const newSection: JournalSection = {
        id: uuidv4(),
        title,
        description: suggestedSection?.description || '',
        type: suggestedSection?.type || JournalSectionType.PARAGRAPHS,
      };

      const updatedSections = [...sections, newSection];
      setSections(updatedSections);
      updateUserConfig(updatedSections);
    };

    const handleShowDeleteConfirmation = (sectionId: string) => {
      if (deletingSectionId === sectionId) {
        setDeletingSectionId(null);
      } else {
        setEditingSectionId(null);
        setIsAddingSection(false);
        setDeletingSectionId(sectionId);
      }
    };

    const confirmDeleteSection = (sectionId: string) => {
      const updatedSections = sections.filter(
        (section) => section.id !== sectionId
      );
      setSections(updatedSections);
      updateUserConfig(updatedSections);
      setDeletingSectionId(null);
    };

    const cancelDeleteConfirmation = () => {
      setDeletingSectionId(null);
    };

    const addSectionDisabled =
      isAddingSection ||
      editingSectionId !== null ||
      deletingSectionId !== null;

    return (
      <YStack className="gap-2">
        <XStack className="justify-between items-center">
          <Label className="text-foreground">Journal Sections</Label>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleAddSection}
            disabled={addSectionDisabled}
          >
            Add Section
          </Button>
        </XStack>

        {isAddingSection && (
          <AddSectionForm
            title={editSectionTitle}
            setTitle={setEditSectionTitle}
            description={editSectionDescription}
            setDescription={setEditSectionDescription}
            type={editSectionType}
            setType={setEditSectionType}
            onSave={handleSaveSection}
            onCancel={handleCancelEdit}
          />
        )}

        {sections.length === 0 && !isAddingSection ? (
          <Empty.Root variant="plain">
            <Empty.Media variant="icon">
              <Icons.list />
            </Empty.Media>
            <Empty.Header>
              <Empty.Title>No sections yet</Empty.Title>
              <Empty.Description>
                Add a section to shape what shows up in your journal.
              </Empty.Description>
            </Empty.Header>
          </Empty.Root>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sections.map((section) => section.id)}
              strategy={verticalListSortingStrategy}
            >
              <YStack className="gap-2 mt-2">
                {sections.map((section, index) => (
                  <JournalSectionItem
                    key={section.id}
                    section={section}
                    index={index}
                    onEdit={handleEditSection}
                    onDelete={handleShowDeleteConfirmation}
                    isDisabled={
                      isAddingSection ||
                      editingSectionId !== null ||
                      deletingSectionId !== null
                    }
                    deletingSectionId={deletingSectionId}
                    editingSectionId={editingSectionId}
                    onCancelDelete={cancelDeleteConfirmation}
                    onConfirmDelete={confirmDeleteSection}
                    editSectionTitle={editSectionTitle}
                    setEditSectionTitle={setEditSectionTitle}
                    editSectionDescription={editSectionDescription}
                    setEditSectionDescription={setEditSectionDescription}
                    editSectionType={editSectionType}
                    setEditSectionType={setEditSectionType}
                    onCancelEdit={handleCancelEdit}
                    onSaveEdit={handleSaveSection}
                  />
                ))}
              </YStack>
            </SortableContext>
          </DndContext>
        )}

        <SuggestedSections
          suggestions={suggestedSections}
          existingSections={sections}
          onAddSuggestion={handleAddSuggestedSection}
        />
      </YStack>
    );
  }
);
